import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

/** The roles this application recognises, most privileged first. */
export type Role = 'superadmin' | 'admin' | 'author' | 'viewer';

/**
 * Identity and role for the signed-in user, read from the Cognito token.
 *
 * Everything here is for shaping the UI — hiding a menu the user cannot use. It is not a security
 * boundary: the API checks the same token and the same groups independently, so a user who edits
 * this in their browser gains nothing but 401s and 403s.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {

  private readonly oidc = inject(OidcSecurityService);

  readonly isLoggedIn$: Observable<boolean> =
    this.oidc.isAuthenticated$.pipe(map(a => a.isAuthenticated));

  /** Email as Cognito knows it, for the account menu. */
  readonly email$: Observable<string | null> =
    this.oidc.userData$.pipe(map(u => u?.userData?.['email'] ?? null));

  /** Group membership from the access token; a user with no group has no role. */
  readonly roles$: Observable<Role[]> = this.oidc.getAccessToken().pipe(
    map(token => AuthService.rolesFromToken(token))
  );

  readonly isAdmin$: Observable<boolean> =
    this.roles$.pipe(map(r => r.includes('admin') || r.includes('superadmin')));

  readonly canWrite$: Observable<boolean> =
    this.roles$.pipe(map(r => r.some(x => x === 'superadmin' || x === 'admin' || x === 'author')));

  login(): void {
    this.oidc.authorize();
  }

  /**
   * Ends the Cognito session as well as the local one — clearing local tokens alone would sign
   * straight back in on the next redirect.
   *
   * Built by hand rather than with logoff(). Cognito advertises an end_session_endpoint, so the
   * library sends the standard OIDC parameters (id_token_hint, post_logout_redirect_uri) — but
   * Cognito's /logout expects client_id and logout_uri. Given the standard shape it bounces to
   * /login and renders "An error was encountered with the requested page." logoffLocal() clears
   * our tokens; the redirect ends the session at Cognito.
   *
   * logout_uri must exactly match a Sign-out URL configured on the app client, trailing slash
   * included, or Cognito shows the same error.
   */
  logout(): void {
    this.oidc.logoffLocal();
    const { logoutUrl, clientId, postLogoutRedirectUri } = environment.auth;
    const url = `${logoutUrl}?client_id=${encodeURIComponent(clientId)}`
              + `&logout_uri=${encodeURIComponent(postLogoutRedirectUri)}`;
    window.location.href = url;
  }

  /**
   * Reads cognito:groups out of the access token. Decoding rather than verifying is deliberate and
   * safe here: this only decides what the UI shows, and the API verifies the signature itself.
   */
  static rolesFromToken(token: string | null | undefined): Role[] {
    if (!token) return [];
    const known: Role[] = ['superadmin', 'admin', 'author', 'viewer'];
    try {
      const payload = token.split('.')[1];
      if (!payload) return [];
      const json = JSON.parse(
        decodeURIComponent(
          atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
      );
      const groups: unknown = json['cognito:groups'];
      if (!Array.isArray(groups)) return [];
      return known.filter(r => groups.includes(r));
    } catch {
      return [];
    }
  }
}
